import { CompanyContactFields } from './company-contact-fields.js';
import { EMPTY_COMPANY_CONTACTS, contactNumbersError } from './company-contact-model.js';
import {
  Building2 as CardIconBuilding2,
  Eye as CardIconEye,
  Image as CardIconImage,
  Palette as CardIconPalette,
} from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { Button, Card, FormErrorSummary, Input, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useId, useState } from 'react';
import {
  companyIdentityApi,
  getApiError,
  getApiErrorField,
  type BrandLogoKind,
  type BrandLogoSet,
  type BrandPalette,
  type CompanyProfile,
} from '../lib/api.js';
import { refreshSession } from '../lib/session.js';
import {
  brandPaletteLabels,
  brandPrintColors,
  brandQueryKey,
  initialsOf,
  useLogoUrl,
} from './brand.js';
import { BrandLogoSlot } from './brand-logo-slot.js';
import { CompanyIdentityMark } from './company-identity-mark.js';
import { logoSlots } from './brand-logo-slots.js';
import type { SocialLink } from '@hue/contracts';
import { SocialLinksEditor } from './social-links-editor.js';
import { socialLinksError } from './social-links-model.js';
import { PageHeader } from './page-title.js';
import { CancellationPolicySection } from '../modules/scheduling/index.js';

const PALETTES = Object.keys(brandPrintColors) as BrandPalette[];
const profileKey = [...brandQueryKey, 'profile'] as const;

type Field = 'displayName' | 'legalName' | 'palette' | 'socialLinks' | 'contactDetails';

/// Dados da empresa (ADR 0040): o nome da marca, a razão social, a cor que vai aos documentos e as
/// logos, além do cadastro institucional próprio. Unidades mantêm endereço e contatos separados.
export function CompanyProfileScreen() {
  const queryClient = useQueryClient();
  const profile = useQuery({ queryKey: profileKey, queryFn: companyIdentityApi.profile });

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: brandQueryKey });
  }

  return (
    <section aria-labelledby="company-profile-title">
      <PageHeader
        trail="Configurações / Dados da empresa"
        id="company-profile-title"
        title="Dados da empresa"
        description={
          <>
            Como a empresa aparece nos documentos impressos, no topo da área da empresa e no ícone
            da aba. A empresa e cada unidade têm CNPJ, endereço e contatos próprios.
          </>
        }
      />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
        {profile.isLoading ? (
          <Card>Carregando os dados da empresa…</Card>
        ) : profile.isError || !profile.data ? (
          <Card>
            <p role="alert" className="text-danger">
              {getApiError(profile.error)}
            </p>
          </Card>
        ) : (
          <>
            <IdentityForm
              // O que o servidor gravou pode diferir do digitado (espaços, razão vazia): o formulário
              // renasce com o valor salvo.
              key={`${profile.data.displayName}|${profile.data.legalName ?? ''}|${profile.data.palette}|${JSON.stringify(profile.data.socialLinks)}|${JSON.stringify(profile.data.contactDetails)}`}
              profile={profile.data}
              onSaved={async (saved) => {
                queryClient.setQueryData(profileKey, saved);
                await refresh();
                await refreshSession(queryClient);
              }}
            />
            <LogosCard
              logos={profile.data.logos}
              canManage={profile.data.canManage}
              onUpload={async (kind, file) => {
                await companyIdentityApi.uploadLogo(kind, file);
                await refresh();
              }}
              onRemove={async (kind) => {
                await companyIdentityApi.removeLogo(kind);
                await refresh();
              }}
            />
            <PreviewCard profile={profile.data} />
            <CancellationPolicySection target={{ kind: 'COMPANY' }} />
          </>
        )}
      </div>
    </section>
  );
}

function IdentityForm({
  profile,
  onSaved,
}: {
  profile: CompanyProfile;
  onSaved: (profile: CompanyProfile) => Promise<void>;
}) {
  const snackbar = useSnackbar();
  const formId = useId();
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [contactDetails, setContactDetails] = useState(
    profile.contactDetails ?? EMPTY_COMPANY_CONTACTS,
  );
  const [legalName, setLegalName] = useState(profile.legalName ?? '');
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>(profile.socialLinks ?? []);
  const [palette, setPalette] = useState<BrandPalette>(profile.palette);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [requestError, setRequestError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const disabled = !profile.canManage;

  async function save() {
    setRequestError(undefined);
    const name = displayName.replace(/\s+/gu, ' ').trim();
    if (name.length < 2) {
      setErrors({ displayName: 'Informe o nome da marca com pelo menos 2 caracteres.' });
      return;
    }
    const contactsError = contactNumbersError(contactDetails.contactNumbers);
    if (contactsError) {
      setErrors({ contactDetails: contactsError });
      return;
    }
    const socialError = socialLinksError(socialLinks);
    if (socialError) {
      setErrors({ socialLinks: socialError });
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const saved = await companyIdentityApi.update({
        displayName: name,
        legalName: legalName.trim() || null,
        palette,
        socialLinks,
        contactDetails,
      });
      await onSaved(saved);
      snackbar.success('Dados da empresa salvos.');
    } catch (cause) {
      const field = getApiErrorField(cause) as Field | undefined;
      if (
        field === 'displayName' ||
        field === 'legalName' ||
        field === 'palette' ||
        field === 'socialLinks' ||
        field === 'contactDetails'
      ) {
        setErrors({ [field]: getApiError(cause) });
      } else {
        setRequestError(getApiError(cause));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="grid gap-5 p-5 sm:p-6">
      <form
        id={formId}
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div>
          <ContentCardHeader title="Nome" icon={<CardIconBuilding2 className="size-4" />} />
          <p className="mt-1 text-sm text-content-muted">
            O nome da marca é o que as clientes veem. A razão social é opcional.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nome da marca"
            value={displayName}
            maxLength={160}
            disabled={disabled}
            error={errors.displayName}
            aria-invalid={errors.displayName ? true : undefined}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <Input
            label="Razão social"
            value={legalName}
            maxLength={200}
            disabled={disabled}
            error={errors.legalName}
            aria-invalid={errors.legalName ? true : undefined}
            hint="Aparece abaixo do nome no timbre, quando não há logo."
            onChange={(event) => setLegalName(event.target.value)}
          />
        </div>

        <div>
          <ContentCardHeader
            title="Cor da empresa"
            id={`${formId}-palette`}
            icon={<CardIconPalette className="size-4" />}
          />
          <p className="mt-1 max-w-2xl text-sm text-content-muted">
            Aparece nos detalhes dos documentos: nome no timbre, linhas, rótulos e o fundo claro dos
            títulos. Não muda a aparência do sistema de ninguém, que continua sendo escolha de cada
            pessoa.
          </p>
        </div>
        <div
          role="radiogroup"
          aria-labelledby={`${formId}-palette`}
          className="flex flex-wrap gap-2.5"
        >
          {PALETTES.map((option) => {
            const selected = option === palette;
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => setPalette(option)}
                className={`flex cursor-pointer items-center gap-2.5 rounded-xl border py-2 pl-2 pr-3.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-60 ${
                  selected
                    ? 'border-brand-500 bg-brand-500/10'
                    : 'border-surface-border bg-surface-elevated hover:border-brand-500/50'
                }`}
              >
                <span
                  className="grid size-7 place-items-center rounded-lg border border-black/10"
                  style={{ backgroundColor: brandPrintColors[option] }}
                >
                  {selected ? <Check className="size-4 text-white" aria-hidden="true" /> : null}
                </span>
                {brandPaletteLabels[option]}
              </button>
            );
          })}
        </div>
        <CompanyContactFields
          value={contactDetails}
          onChange={setContactDetails}
          disabled={disabled || saving}
        />
        {errors.contactDetails ? (
          <p role="alert" className="text-sm text-danger">
            {errors.contactDetails}
          </p>
        ) : null}
        <SocialLinksEditor
          value={socialLinks}
          onChange={setSocialLinks}
          disabled={disabled || saving}
          error={errors.socialLinks}
        />
        {errors.palette ? (
          <p role="alert" className="text-sm text-danger">
            {errors.palette}
          </p>
        ) : null}
      </form>
      {profile.canManage ? (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
          <FormErrorSummary form={formId} className="basis-full sm:basis-auto sm:flex-1" />
          {requestError ? (
            <p role="alert" className="text-sm text-danger">
              {requestError}
            </p>
          ) : null}
          <Button type="submit" form={formId} loading={saving}>
            Salvar dados da empresa
          </Button>
        </div>
      ) : (
        <p className="text-sm text-content-muted">
          Só quem pode alterar os dados da empresa muda nome, cor, logos e redes sociais.
        </p>
      )}
    </Card>
  );
}

function LogosCard({
  logos,
  canManage,
  onUpload,
  onRemove,
}: {
  logos: BrandLogoSet;
  canManage: boolean;
  onUpload: (kind: BrandLogoKind, file: File) => Promise<void>;
  onRemove: (kind: BrandLogoKind) => Promise<void>;
}) {
  const snackbar = useSnackbar();
  return (
    <Card className="grid gap-5 p-5 sm:p-6">
      <div>
        <ContentCardHeader title="Logos" icon={<CardIconImage className="size-4" />} />
        <p className="mt-1 text-sm text-content-muted">
          Suba um arquivo por formato; o sistema gera os tamanhos. Aceita SVG, PNG ou WebP. Sem
          arquivo, entra o nome da marca em texto.
        </p>
      </div>
      <div
        role="note"
        className="max-w-3xl rounded-xl border border-brand-500/35 bg-brand-500/10 px-4 py-3 text-sm"
      >
        <strong>Prefira o SVG.</strong> Ele é desenhado em vetor e fica nítido em qualquer tamanho:
        no PDF impresso, no topo da área da empresa e no ícone da aba. PNG e WebP perdem definição
        quando ampliados. Se a sua agência ou designer entregou a logo, peça o arquivo{' '}
        <strong>.svg</strong>.
      </div>
      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {logoSlots.map((slot) => (
          <BrandLogoSlot
            key={slot.kind}
            kind={slot.kind}
            logo={logos[slot.kind]}
            canManage={canManage}
            onUpload={async (file) => {
              await onUpload(slot.kind, file);
              snackbar.success(`${slot.title} enviada.`);
            }}
            onRemove={async () => {
              await onRemove(slot.kind);
              snackbar.success(`${slot.title} removida.`);
            }}
          />
        ))}
      </div>
    </Card>
  );
}

/// Onde a identidade aparece, com o que está gravado: o timbre do PDF e o topo da área da empresa.
function PreviewCard({ profile }: { profile: CompanyProfile }) {
  const color = brandPrintColors[profile.palette];
  const mainUrl = useLogoUrl(profile.logos.MAIN, 'small');
  const symbolUrl = useLogoUrl(profile.logos.SYMBOL, 'small');

  return (
    <Card className="grid gap-5 p-5 sm:p-6">
      <div>
        <ContentCardHeader title="Onde aparece" icon={<CardIconEye className="size-4" />} />
        <p className="mt-1 text-sm text-content-muted">
          Prévia com o que está salvo. Endereço e contato do timbre vêm da unidade que emite o
          documento.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2 2xl:grid-cols-3">
        <figure className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2">
          <div className="grid min-h-48 content-start gap-2.5 rounded-md border border-[#d8d8d4] bg-white p-5 text-[11px] text-[#1b1b1f] shadow-sm">
            <div
              className="flex items-end justify-between gap-3 border-b-[1.5px] pb-2"
              style={{ borderColor: color }}
            >
              {mainUrl ? (
                <img
                  src={mainUrl}
                  alt="Logo principal no timbre"
                  className="max-h-9 max-w-[150px] object-contain"
                />
              ) : (
                <div>
                  <strong className="block text-[15px]" style={{ color }}>
                    {profile.displayName}
                  </strong>
                  {profile.legalName ? (
                    <span className="text-[9px] text-[#55555c]">{profile.legalName}</span>
                  ) : null}
                </div>
              )}
              <span className="text-right text-[10px] leading-snug text-[#55555c]">
                Endereço e contato
                <br />
                da unidade
              </span>
            </div>
            <div
              className="rounded-sm px-2 py-1 text-xs font-bold"
              style={{ backgroundColor: `color-mix(in srgb, ${color} 9%, #fff)` }}
            >
              Ficha de anamnese
            </div>
            <p>
              <b style={{ color }}>Cliente</b> Mariana Albuquerque Souza
            </p>
          </div>
          <figcaption className="text-xs text-content-muted">PDF, primeira página.</figcaption>
        </figure>
        <figure className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2">
          <div className="grid min-h-48 content-start gap-2.5 rounded-md border border-[#d8d8d4] bg-white p-5 text-[11px] text-[#1b1b1f] shadow-sm">
            <div
              className="flex items-center justify-between border-b pb-1.5"
              style={{ borderColor: color }}
            >
              {symbolUrl ? (
                <img src={symbolUrl} alt="Símbolo no timbre" className="size-6 object-contain" />
              ) : (
                <span
                  className="grid size-6 place-items-center rounded text-[10px] font-bold text-white"
                  style={{ backgroundColor: color }}
                >
                  {initialsOf(profile.displayName)}
                </span>
              )}
              <span className="text-[#55555c]">página 2</span>
            </div>
            <div
              className="rounded-sm px-2 py-1 text-xs font-bold"
              style={{ backgroundColor: `color-mix(in srgb, ${color} 9%, #fff)` }}
            >
              Histórico de saúde
            </div>
          </div>
          <figcaption className="text-xs text-content-muted">PDF, páginas seguintes.</figcaption>
        </figure>
        <figure className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2">
          <div
            aria-label="Prévia do topo"
            role="img"
            className="grid min-h-36 content-start overflow-hidden rounded-xl border border-surface-border bg-surface"
          >
            <div className="flex h-14 items-center justify-center border-b border-surface-border bg-surface-card px-3">
              <CompanyIdentityMark name={profile.displayName} logos={profile.logos} />
            </div>
          </div>
          <figcaption className="text-xs text-content-muted">
            Topo da área da empresa, no tema atual. No escuro sem logo própria, a principal vai
            sobre uma faixa clara.
          </figcaption>
        </figure>
      </div>
    </Card>
  );
}
